import { useNavigate, useParams } from "react-router-dom";
import { useGetAccountById } from "@/services/queries/accounts";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { CaretLeft } from "@phosphor-icons/react";
import Loading from "@/components/custom/loading";
import { currencyFormatter, formatDate } from "@/lib/utils";
import {
  useGetCurrencies,
  useGetCurrencyById,
} from "@/services/queries/currency";
import { ACCOUNT_TYPES, TRANSACTION_STATUS } from "@/lib/constants";
import { Currency, Expense, Transaction } from "@/lib/types";
import { Account } from "@/lib/types";
import { useGetTransactionsBySearch } from "@/services/queries/transactions";
import Card from "@/components/custom/card";

const BalanceDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data: accountData, isPending, isError } = useGetAccountById(id);
  const navigate = useNavigate();

  const { data: currencyData } = useGetCurrencyById(
    accountData?.value.currencyId
  );

  const account = useMemo(() => {
    return accountData?.value;
  }, [accountData]);

  const currency = useMemo(() => {
    return currencyData?.value;
  }, [currencyData]);

  return (
    <>
      <title>{t("title.balance_detail")}</title>

      <div className="px-5">
        <button
          data-testid="balance-detail-back-button"
          onClick={() => navigate("/balance")}
        >
          <CaretLeft size={20} color="#FFFFFF" />
        </button>

        {isError ? (
          <div className="mt-10">
            <h1 className="text-center text-2xl font-medium text-white">
              {t("error_message.account_not_found")}
            </h1>
          </div>
        ) : isPending ? (
          <Loading className="mt-10" />
        ) : (
          <div className="mt-10">
            <Header account={account} currency={currency} />
            <Transactions accountId={id} />
          </div>
        )}
      </div>
    </>
  );
};

const Header = ({
  account,
  currency,
}: {
  account?: Account;
  currency?: Currency;
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-8 flex flex-col">
      <h1 className="text-3xl font-semibold text-white">{account?.name}</h1>
      <span className="text-sm text-white/70">
        {t("label.account_number")} : {account?.accountNumber}
      </span>
      <span className="mt-2 text-2xl text-white">
        <span data-testid="balance-amount">
          {currencyFormatter(account?.currentBalance ?? 0, currency?.code)}
        </span>{" "}
        -{" "}
        {t(
          `account_type.${
            ACCOUNT_TYPES.find((type) => type.value === account?.type)
              ?.translationKey
          }`
        )}
      </span>
    </div>
  );
};

const Transactions = ({ accountId }: { accountId?: string }) => {
  const { t } = useTranslation();
  const { data: currenciesData } = useGetCurrencies();
  const { data: transactionsData, isPending } = useGetTransactionsBySearch({
    searchParams: `accountId=${accountId}`,
    options: {
      enabled: !!accountId,
    },
  });

  const transactions = useMemo(() => {
    return transactionsData?.value.items;
  }, [transactionsData]);

  const currencies = useMemo(() => {
    return currenciesData?.value;
  }, [currenciesData]);

  return (
    <div>
      <h3 className="mb-4 text-xl font-medium text-white">
        {t("label.transactions")}
      </h3>

      <div className="flex flex-col gap-2">
        {isPending ? (
          <Loading />
        ) : transactions && transactions.length > 0 ? (
          transactions.map((transaction) => (
            <TransactionItem
              key={transaction.id}
              transaction={transaction}
              currencies={currencies ?? []}
            />
          ))
        ) : (
          <div className="mt-10">
            <h1 className="text-center text-sm font-medium text-white">
              {t("error_message.not_found_transactions")}
            </h1>
          </div>
        )}
      </div>
    </div>
  );
};

const TransactionItem = ({
  transaction,
  currencies,
}: {
  transaction: Transaction;
  currencies: Currency[];
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currency = useMemo(() => {
    return currencies?.find(
      (currency) =>
        "currencyId" in transaction && currency.id === transaction.currencyId
    );
  }, [currencies, transaction]);

  const transactionStatus = useMemo(() => {
    return TRANSACTION_STATUS.find(
      (status) => status.value === transaction.status
    );
  }, [transaction.status]);

  return (
    <Card
      key={transaction.id}
      data-testid={`transaction-item-${transaction.id}`}
      onClick={() =>
        navigate(`/transaction/${transaction.id}`, {
          state: {
            redirectPath: "balance/" + id,
          },
        })
      }
    >
      <div className="flex flex-col items-start justify-start gap-2 text-white">
        <div>
          {transaction.description ? (
            <span className="text-sm font-medium leading-[14px] text-white">
              {transaction.description}
            </span>
          ) : (transaction as Expense).vendorName ? (
            <span className="text-sm font-medium leading-[14px] text-white">
              {(transaction as Expense).vendorName}
            </span>
          ) : null}
        </div>

        <span className="text-xs font-medium leading-[9px] text-white/50">
          {formatDate(transaction.transactionDate, "dd/MM/yyyy")}
        </span>
      </div>
      <div className="flex items-center gap-[6px]">
        <span className="text-sm text-[#7ECEE8]">
          {currencyFormatter(transaction.amount, currency?.code)}
        </span>
        <span style={{ color: transactionStatus?.color }}>
          {transactionStatus?.icon}
        </span>
      </div>
    </Card>
  );
};

export default BalanceDetail;
